import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import {
  configApiRef,
  githubAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';

import { makeStyles, Typography } from '@material-ui/core';
import { PopupButton } from 'react-calendly';

import { pocteo } from './themes/pocteo';
import {UnifiedThemeProvider} from "@backstage/theme";
import { DatabasePage } from '@internal/plugin-database';
import { EnvironmentPage } from '@internal/plugin-environment';
import { SelfServicePage } from '@internal/plugin-self-service';
import { CredentialsPage } from '@internal/plugin-credentials';
import { AiChatPage } from '@internal/plugin-ai';
import { WorkloadPage } from '@internal/plugin-workload';
import { ClusterPage } from '@internal/plugin-cluster';
import { AddonPage } from '@internal/plugin-addon';
import { CostPage } from '@internal/plugin-cost';
import { WorkflowPage } from '@internal/plugin-workflow';
import { CatalogOverviewPage } from '@internal/plugin-catalog-overview';
import { ScorecardPage } from '@internal/plugin-scorecard';
import {
  ActivityPage,
  ActivitySessionRecorder,
} from '@internal/plugin-activity';
import { RbacPage, NoAccessPage } from '@internal/plugin-rbac';
import type { BasicPermission } from '@backstage/plugin-permission-common';
import {
  activityReadPermission,
  addonReadPermission,
  aiUsePermission,
  catalogPageReadPermission,
  clusterReadPermission,
  costReadPermission,
  credentialsReadPermission,
  databaseReadPermission,
  environmentReadPermission,
  rbacReadPermission,
  scorecardReadPermission,
  selfServiceReadPermission,
  workflowReadPermission,
  workloadReadPermission,
} from '@internal/plugin-rbac-common';
import { ExplorePage } from '@backstage-community/plugin-explore';
import { AnnouncementsPage } from '@backstage-community/plugin-announcements';
import { DevToolsPage } from '@backstage/plugin-devtools';
import { CatalogUnprocessedEntitiesPage } from '@backstage/plugin-catalog-unprocessed-entities';

function LightIcon() {
    return null;
}

const useStyles = makeStyles(theme => ({
    container: {
        display: 'flex',
        height: '100vh',
        width: '100vw',
        backgroundColor: '#fff',
    },
    leftPanel: {
        flex: 1,
        position: 'relative',
        background: 'linear-gradient(180deg, hsl(237, 53%, 8%), hsl(242, 83%, 16%))',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        padding: theme.spacing(4),
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `radial-gradient(rgba(255, 255, 255, 0.15) 1.5px, transparent 1.5px)`,
            backgroundSize: '40px 40px',
            maskImage: 'radial-gradient(circle at center, black, transparent 80%)',
            zIndex: 1,
        },
    },
    geometricOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 2,
        pointerEvents: 'none',
        '& .shape': {
            position: 'absolute',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            background: 'rgba(255, 255, 255, 0.02)',
            backdropFilter: 'blur(4px)',
        },
    },
    logo: {
        height: '250px',
        width: 'auto',
        marginBottom: theme.spacing(4),
        filter: 'drop-shadow(0px 20px 40px rgba(0,0,0,0.5))',
        zIndex: 10,
        transition: 'transform 0.5s ease-in-out',
        '&:hover': {
            transform: 'scale(1.05)',
        }
    },
    title: {
        fontFamily: 'Space Grotesk, sans-serif',
        fontWeight: 700,
        letterSpacing: '0.4em',
        textTransform: 'uppercase',
        zIndex: 10,
        fontSize: '3rem',
        textShadow: '0px 4px 10px rgba(0,0,0,0.3)',
    },
    rightPanel: {
        width: '30%',
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        zIndex: 20,
        paddingTop: '25vh',
        '& h1': {
            display: 'none !important',
        },
        '& div > button': {
            backgroundColor: '#000000 !important',
            color: '#ffffff !important',
            padding: '8px 32px !important',
            textTransform: 'none !important',
            fontWeight: 400,
            fontSize: '0.9rem',
            width: '100%',
            transition: 'background-color 0.2s ease',
            '&:hover': {
                backgroundColor: '#333333 !important',
            },

            // Inject the icon manually via CSS to ensure perfect alignment with hidden label
            '&::before': {
                content: '""',
                display: 'block',
                width: '30px',
                height: '30px',
                marginRight: '15  px',
                backgroundColor: '#fff',
                maskImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12\'/%3E%3C/svg%3E")',
                maskRepeat: 'no-repeat',
                maskSize: 'contain',
            }
        },
        '& ul':{
            width: '400px',
            background: '#fff',
            color: '#fff',
        },
        '& ul > li > div': {
            backgroundColor: '#fff',
            shadow: 'none',
        },
        '& header': {
            display: 'none !important',
        }
    },
    footerLeft: {
        position: 'absolute',
        bottom: theme.spacing(3),
        left: theme.spacing(4),
        color: 'rgba(255, 255, 255, 0.5)',
        fontSize: '0.85rem',
        zIndex: 10,
        fontFamily: 'Space Grotesk, sans-serif',
    },
    footerRight: {
        position: 'absolute',
        bottom: theme.spacing(3),
        right: theme.spacing(4),
        zIndex: 10,
        '& a': {
            color: 'rgba(255, 255, 255, 0.8)',
            textDecoration: 'none',
            fontSize: '0.85rem',
            fontFamily: 'Space Grotesk, sans-serif',
            transition: 'color 0.3s ease',
            '&:hover': {
                color: '#fff',
                textDecoration: 'underline',
            },
        },
    },
    demoLink: {
        marginBottom: theme.spacing(4), // Sécurité pour l'affichage en bas
        background: 'none !important',
        border: 'none !important',
        padding: '0 !important',
        color: '#666 !important', // Gris discret
        textDecoration: 'underline',
        cursor: 'pointer',
        fontSize: '0.9rem',
        fontFamily: 'inherit',
        transition: 'color 0.2s ease',
        '&:hover': {
            color: '#000 !important', // Devient noir au survol
        },
    },
}));

const MySignInPage = (props: any) => {
    const classes = useStyles();
    const configApi = useApi(configApiRef);

    // Config decides, not the build. demo.pocteo.tn wants a Guest button so a
    // visitor can look around without a GitHub account; the backend's guest
    // provider has to be enabled there too, and what a guest may actually do
    // is the 'viewer' role's business, not this flag's.
    const guestSignIn = configApi.getOptionalBoolean('app.guestSignIn') ?? false;

    return (
            <div className={classes.container}>
                {/* Côté Gauche : Votre design */}
                <div className={classes.leftPanel}>
                    <div className={classes.geometricOverlay}>
                        <div className="shape circle" />
                        <div className="shape triangle" />
                    </div>
                    <img src="/pocteo-platform-white.svg" alt="Pocteo Logo" className={classes.logo} />

                    <Typography className={classes.footerLeft}>
                        ©2026 Pocteo. All rights reserved.
                    </Typography>

                    {/* Lien en bas à droite */}
                    <Typography className={classes.footerRight}>
                        <a href="https://pocteo.dev" target="_blank" rel="noopener noreferrer">
                            https://pocteo.dev
                        </a>
                    </Typography>
                </div>

                {/* Côté Droit : On laisse Backstage gérer le bouton via son composant natif
                mais on le "nettoie" avec les styles rightPanel que nous avons créés */}
                <div className={classes.rightPanel}>
                    <img
                        src="/pocteo-platform-blue.png"
                        alt="Pocteo Platform"
                        style={{ height: '80px' }}
                    />
                    <SignInPage
                        {...props}
                        providers={[
                            ...(guestSignIn ? (['guest'] as const) : []),
                            {
                                id: 'github-auth-provider',
                                title: 'Welcome Back!',
                                message: 'Connect to Pocteo Platform with Github',
                                apiRef: githubAuthApiRef,
                            },
                        ]}
                    />
                    <PopupButton
                        url="https://calendly.com/pocteo/let-s-talk"
                        rootElement={document.getElementById("root")!}
                        text="Book a Demo"
                        className={classes.demoLink}
                    />
                </div>
            </div>
    );
};

const app = createApp({
  apis,
    themes: [{
        id: 'pocteo',
        title: 'Pocteo Theme',
        variant: 'light',
        icon: <LightIcon />,
        Provider: ({ children }) => (
            <UnifiedThemeProvider theme={pocteo} children={children} />
        ),
    }],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
    components: {
        SignInPage: props => (
            <MySignInPage {...props} />
        ),
    },
});

/**
 * Hiding a sidebar item is a courtesy; this is the part that matters in the
 * browser. Typing the URL of a feature you were not granted lands on a page
 * that names the missing permission rather than a blank screen.
 *
 * The backend refuses the same calls independently — neither layer is
 * trusting the other.
 */
const guard = (permission: BasicPermission, element: JSX.Element) => (
  <RequirePermission
    permission={permission}
    errorPage={<NoAccessPage permissionName={permission.name} />}
  >
    {element}
  </RequirePermission>
);

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route
      path="/catalog-overview"
      element={guard(catalogPageReadPermission, <CatalogOverviewPage />)}
    />
    <Route
      path="/scorecards"
      element={guard(scorecardReadPermission, <ScorecardPage />)}
    />
    <Route path="/explore" element={<ExplorePage />} />
    <Route path="/announcements" element={<AnnouncementsPage />} />
    {/* Catalog entities that failed to process, which is otherwise only
        visible in the backend log. */}
    <Route path="/catalog-unprocessed-entities" element={<CatalogUnprocessedEntitiesPage />} />
    <Route path="/devtools" element={<DevToolsPage />} />
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route
      path="/database"
      element={guard(databaseReadPermission, <DatabasePage />)}
    />
    <Route
      path="/environments"
      element={guard(environmentReadPermission, <EnvironmentPage />)}
    />
    <Route
      path="/self-service"
      element={guard(selfServiceReadPermission, <SelfServicePage />)}
    />
    <Route
      path="/credentials"
      element={guard(credentialsReadPermission, <CredentialsPage />)}
    />
    <Route
      path="/ai"
      element={guard(aiUsePermission, <AiChatPage />)}
    />
    <Route
      path="/workloads"
      element={guard(workloadReadPermission, <WorkloadPage />)}
    />
    <Route
      path="/clusters"
      element={guard(clusterReadPermission, <ClusterPage />)}
    />
    <Route
      path="/addons"
      element={guard(addonReadPermission, <AddonPage />)}
    />
    <Route
      path="/cost"
      element={guard(costReadPermission, <CostPage />)}
    />
    <Route
      path="/pipelines"
      element={guard(workflowReadPermission, <WorkflowPage />)}
    />
    <Route path="/rbac" element={guard(rbacReadPermission, <RbacPage />)} />
    <Route
      path="/activity"
      element={guard(activityReadPermission, <ActivityPage />)}
    />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    {/* Renders nothing. Records signing in and out, which the analytics
        API cannot observe on its own. */}
    <ActivitySessionRecorder />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
